import React, { useEffect, useState } from "react";
import {
    Newspaper,
    Plus,
    Pencil,
    Trash2,
    PlayCircle,
    RefreshCw,
    Loader2,
    Activity,
    X,
    Check,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

export default function NewsSources() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/news-automation/sources");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = () => setEditing({
        name: "",
        type: "rss",
        url: "",
        fetch_interval_hours: 6,
        is_active: true,
        description: "",
        isNew: true,
    });

    const save = async () => {
        if (!editing.name || !editing.url) {
            toast.error("Ad ve URL zorunlu.");
            return;
        }
        try {
            const payload = {
                id: editing.isNew ? undefined : editing.id,
                name: editing.name,
                type: editing.type || "rss",
                url: editing.url,
                fetch_interval_hours: editing.fetch_interval_hours || 6,
                is_active: editing.is_active !== false,
                description: editing.description || "",
            };
            await api.post("/admin/news-automation/sources", payload);
            toast.success("Kaydedildi.");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Kaynak silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/news-automation/sources/${id}`);
            toast.success("Silindi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const testFetch = async (id) => {
        setBusy(id);
        try {
            const { data } = await api.post(`/admin/news-automation/sources/${id}/test`);
            const f = data.fetched ?? 0;
            const dup = data.duplicate ?? 0;
            if (data.error) toast.error(`Hata: ${data.error}`);
            else toast.success(`Test başarılı: ${f} yeni, ${dup} duplicate`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const fetchAll = async () => {
        setBusy("all");
        try {
            const { data } = await api.post("/admin/news-automation/sources/fetch-now");
            toast.success(`Çekildi: ${data.fetched} yeni / ${data.duplicate} dup / ${data.errors} hata`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div data-testid="admin-news-sources-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Newspaper className="w-6 h-6 text-teal" /> Haber Kaynakları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        RSS akışlarından otomatik haber toplama. Tüm kaynaklar 6 saatte bir taranır (scheduler aktifse).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchAll}
                        disabled={busy === "all"}
                        className="bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="news-sources-fetch-all"
                    >
                        {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Tümünü Şimdi Çek
                    </button>
                    <button
                        onClick={create}
                        className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="news-sources-new-btn"
                    >
                        <Plus className="w-4 h-4" /> Yeni Kaynak
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Henüz kaynak yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="news-sources-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Ad / URL</th>
                                <th className="px-4 py-3">Tip</th>
                                <th className="px-4 py-3">Aralık</th>
                                <th className="px-4 py-3">Son Çekim</th>
                                <th className="px-4 py-3">Toplam</th>
                                <th className="px-4 py-3">Aktif</th>
                                <th className="px-4 py-3 w-44">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="border-t border-slate-100">
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-dark-navy">{s.name}</div>
                                        <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-teal-700 hover:underline truncate block max-w-[280px]">{s.url}</a>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${s.type === "rss" ? "bg-teal/10 text-teal-700" : "bg-amber-100 text-amber-700"}`}>{s.type || "rss"}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-data">{s.fetch_interval_hours || 6}h</td>
                                    <td className="px-4 py-3 text-xs text-deep-blue/70">{s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString("tr-TR") : "—"}</td>
                                    <td className="px-4 py-3 text-xs font-data">
                                        <div>{s.total_fetched || 0}</div>
                                        <div className="text-[10px] text-data-green">{s.total_published || 0} yayın</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block w-2 h-2 rounded-full ${s.is_active ? "bg-data-green" : "bg-slate-400"}`}></span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => testFetch(s.id)}
                                                disabled={busy === s.id}
                                                title="Test Çek"
                                                className="p-1.5 rounded-md hover:bg-light-bg disabled:opacity-50"
                                                data-testid={`news-source-test-${s.id}`}
                                            >
                                                {busy === s.id ? <Loader2 className="w-4 h-4 animate-spin text-teal" /> : <PlayCircle className="w-4 h-4 text-teal-700" />}
                                            </button>
                                            <button onClick={() => setEditing({ ...s, isNew: false })} title="Düzenle" className="p-1.5 rounded-md hover:bg-light-bg">
                                                <Pencil className="w-4 h-4 text-deep-blue" />
                                            </button>
                                            <button onClick={() => remove(s.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                <Trash2 className="w-4 h-4 text-data-red" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="news-source-editor">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-xl w-full my-8">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-dark-navy">{editing.isNew ? "Yeni Kaynak" : "Kaynak Düzenle"}</h2>
                            <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-light-bg">
                                <X className="w-4 h-4 text-deep-blue" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <Field label="Ad">
                                <input
                                    value={editing.name || ""}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    placeholder="Turizm Gazetesi"
                                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                    data-testid="news-source-name"
                                />
                            </Field>
                            <Field label="Tip">
                                <div className="flex gap-2">
                                    {["rss", "api"].map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => setEditing({ ...editing, type: t })}
                                            className={`px-4 py-2 rounded-md text-sm font-semibold ${editing.type === t ? "bg-dark-navy text-white" : "bg-light-bg text-deep-blue"}`}
                                            data-testid={`news-source-type-${t}`}
                                        >
                                            {t.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                                {editing.type === "api" && (
                                    <p className="mt-2 text-xs text-amber-700">API adapter (NewsAPI/WorldNews) henüz aktif değil — şimdilik RSS kullanın.</p>
                                )}
                            </Field>
                            <Field label="URL">
                                <input
                                    value={editing.url || ""}
                                    onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                                    placeholder="https://example.com/rss"
                                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono"
                                    data-testid="news-source-url"
                                />
                            </Field>
                            <Field label="Çekim Aralığı (saat)">
                                <input
                                    type="number"
                                    value={editing.fetch_interval_hours || 6}
                                    onChange={(e) => setEditing({ ...editing, fetch_interval_hours: parseInt(e.target.value, 10) || 6 })}
                                    min={1}
                                    max={72}
                                    className="w-32 px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </Field>
                            <Field label="Açıklama (opsiyonel)">
                                <input
                                    value={editing.description || ""}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </Field>
                            <div className="flex items-center justify-between bg-light-bg rounded-md p-3">
                                <span className="text-sm font-semibold text-dark-navy">Aktif</span>
                                <Switch
                                    checked={editing.is_active !== false}
                                    onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md bg-light-bg text-deep-blue text-sm font-semibold">İptal</button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                                data-testid="news-source-save"
                            >
                                <Check className="w-4 h-4" /> Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
